'use client';

import { useMemo } from 'react';
import { useOperationCosts, type EstimateLine } from './useOperationCost';
import type { OperationType } from '../types/config';

// Average characters per word (Russian ~6, English ~5)
const AVG_CHARS_PER_WORD = 6;

/**
 * Estimates block count from target story duration in minutes.
 * Short stories (<7 min): 3 sentences/block, 5 blocks/min
 * Long stories (>=7 min): 5 sentences/block, 3 blocks/min
 * Matches TextGenerator.ts formula.
 */
import { estimateStoryBlockCount, estimateStoryShape } from '@/contexts/stories/utils/storyShape';

function estimateBlockCount(durationMinutes: number, ageGroup?: string): number {
  // Single source of truth — the same formula drives the engine bundle.
  return estimateStoryBlockCount(durationMinutes, ageGroup);
}

function estimateWordsPerBlock(durationMinutes: number): number {
  const sentencesPerBlock = durationMinutes >= 7 ? 5 : 3;  // superseded by estimateStoryShape
  return sentencesPerBlock * 14; // ~14 words per sentence avg
}

// Average characters per block for TTS (dynamic)
function estimateTtsCharsPerBlock(durationMinutes: number): number {
  return estimateWordsPerBlock(durationMinutes) * AVG_CHARS_PER_WORD;
}

interface StoryEstimate {
  /** Estimated number of text blocks */
  blockCount: number;
  /** Token cost for story text generation */
  storyCost: number;
}

interface AudioEstimate {
  /** Number of audio blocks */
  blockCount: number;
  /** Token cost per audio block */
  costPerBlock: number;
  /** Total audio generation cost */
  totalCost: number;
}

interface ImageEstimate {
  /** Number of page images */
  pageCount: number;
  /** Number of character portraits (average 4) */
  portraitCount: number;
  /** 1 cover image */
  coverCount: number;
  /** Token cost per image */
  costPerImage: number;
  /** Total image generation cost */
  totalCost: number;
}

interface AnalysisEstimate {
  /** Token cost for story analysis before book creation */
  analysisCost: number;
}

interface TranslationEstimate {
  /** Token cost for translating an existing story into another language */
  translationCost: number;
}

interface CostEstimateResult {
  story: StoryEstimate;
  audio: AudioEstimate;
  images: ImageEstimate;
  analysis: AnalysisEstimate;
  translation: TranslationEstimate;
  /** Total estimated cost across all selected operations */
  total: number;
  /** Whether pricing data is still loading */
  loading: boolean;
}

interface CostEstimateOptions {
  /** Target story duration (3-10 minutes). Used to estimate block count when knownBlockCount is not set. */
  durationMinutes: number;
  /** Age group refines block rhythm (1-3 → shorter blocks). */
  ageGroup?: string;
  /** Whether audio generation is planned */
  includeAudio: boolean;
  /** Whether book/image generation is planned */
  includeBook: boolean;
  /** Average number of character portraits (default: 4) */
  avgPortraits?: number;
  /** Actual block count (for existing stories). Overrides duration-based estimate. */
  knownBlockCount?: number;
  /** Exact total characters of an existing story's blocks. */
  knownTotalChars?: number;
  /** Story language — drives words-per-minute in the predicted shape. */
  language?: string;
  /** Whether audio blocks will be auto-generated (default: true). When false, audio cost is 0. */
  autoGenerateAudio?: boolean;
  /** Whether page images will be auto-generated (default: true). When false, page image cost is 0. */
  autoGenerateImages?: boolean;
  /** Whether character portraits will be auto-generated (default: true). When false, portrait cost is 0. */
  autoGeneratePortraits?: boolean;
  /** Whether story text generation is needed (default: true). Set to false for extend/existing story scenarios. */
  includeStory?: boolean;
  /** Whether the existing story is being translated into another language. */
  includeTranslation?: boolean;
}

/**
 * Hook for estimating total content creation costs.
 * Used in wizard to show approximate costs before creation starts.
 *
 * @param durationMinutesOrOptions - Duration in minutes OR options object
 * @param includeAudio - Whether audio generation is planned (legacy)
 * @param includeBook - Whether book/image generation is planned (legacy)
 * @param avgPortraits - Average number of character portraits (default: 4) (legacy)
 */
export function useCostEstimate(
  durationMinutesOrOptions: number | CostEstimateOptions,
  includeAudio?: boolean,
  includeBook?: boolean,
  avgPortraits?: number
): CostEstimateResult {
  // Support both legacy (positional) and new (options object) calling conventions
  const opts: CostEstimateOptions = typeof durationMinutesOrOptions === 'number'
    ? {
        durationMinutes: durationMinutesOrOptions,
        includeAudio: includeAudio ?? false,
        includeBook: includeBook ?? false,
        avgPortraits: avgPortraits ?? 4,
      }
    : durationMinutesOrOptions;

  // SIZE prediction (expected size — NOT price knowledge): predicted for new
  // stories, exact (known*) facts for existing ones. Drives block count + the
  // per-block char length we forward to accounts as the estimate `units`.
  const shape = estimateStoryShape(opts.durationMinutes, opts.ageGroup, opts.language);
  const blockCount = opts.knownBlockCount ?? shape.blockCount;
  const totalChars = opts.knownTotalChars ?? shape.totalChars;
  const avgCharsPerBlock = Math.max(1, Math.round(totalChars / blockCount));

  // PRICE knowledge now lives in ACCOUNTS (#79): forward {op_type, provider, model,
  // units} built from the active model-selection config + the expected size and show
  // the returned credits. Fixed order — read back by index below.
  const lines: EstimateLine[] = [
    { opType: 'story_pipeline' },                                    // story text (flat llm hold)
    { opType: 'story_analysis' },                                    // analysis (flat llm hold)
    { opType: 'audio_block_generation', textLength: avgCharsPerBlock }, // TTS per block (priced)
    { opType: 'page_generation', imageCount: 1 },                    // image per unit (priced)
    { opType: 'story_translation' },                                 // translation (flat llm hold)
  ];
  const { costs, loading } = useOperationCosts(lines);
  const [storyUnit, analysisUnit, audioUnit, imageUnit, translationUnit] = costs;

  return useMemo(() => {
    const shouldAutoAudio = opts.autoGenerateAudio ?? true;
    const shouldAutoImages = opts.autoGenerateImages ?? true;
    const shouldAutoPortraits = opts.autoGeneratePortraits ?? true;
    const shouldIncludeStory = opts.includeStory ?? true;
    const portraits = opts.avgPortraits ?? 4;

    // Story text generation cost (accounts estimate; flat llm hold) — only for new stories
    const storyCost = shouldIncludeStory ? storyUnit : 0;

    // Audio: per-block credits from accounts × block count.
    const audioCostPerBlock = audioUnit;
    const includeAudioCost = opts.includeAudio && shouldAutoAudio;
    const audioTotal = includeAudioCost ? blockCount * audioCostPerBlock : 0;

    // Images: pages + portraits + cover, each costs imagePerUnit tokens (accounts estimate).
    // Cover is generated as part of page generation — only when autoGenerateImages is on
    const imagePerUnit = imageUnit;
    const coverCount = opts.includeBook && shouldAutoImages ? 1 : 0;
    const portraitCount = opts.includeBook && shouldAutoPortraits ? portraits : 0;
    const pageCount = opts.includeBook && shouldAutoImages ? blockCount : 0;
    const imageTotal = (pageCount + portraitCount + coverCount) * imagePerUnit;

    // Story analysis cost (required before book creation)
    const analysisCost = opts.includeBook ? analysisUnit : 0;

    // Translation cost (accounts estimate for the dedicated story_translation op)
    const translationCost = opts.includeTranslation ? translationUnit : 0;

    return {
      story: { blockCount, storyCost },
      audio: {
        blockCount: includeAudioCost ? blockCount : 0,
        costPerBlock: audioCostPerBlock,
        totalCost: audioTotal,
      },
      images: {
        pageCount,
        portraitCount,
        coverCount,
        costPerImage: imagePerUnit,
        totalCost: imageTotal,
      },
      analysis: { analysisCost },
      translation: { translationCost },
      total: storyCost + audioTotal + imageTotal + analysisCost + translationCost,
      loading,
    };
  }, [opts.durationMinutes, opts.knownBlockCount, opts.includeAudio, opts.includeBook,
      opts.avgPortraits, opts.autoGenerateAudio, opts.autoGenerateImages, opts.autoGeneratePortraits,
      opts.includeStory, opts.includeTranslation, blockCount,
      storyUnit, analysisUnit, audioUnit, imageUnit, translationUnit, loading]);
}

/**
 * Hook for calculating exact cost of a known set of items.
 * Used when the exact count is known (e.g. "Generate All Audio" for N blocks).
 *
 * Sources the per-unit token cost from ACCOUNTS (#79) via the same estimate seam —
 * no portal-local sell-rate. `analysis` is a single flat op (count ignored); the
 * others are per-unit × count.
 */
const EXACT_OP_MAP: Record<'audio' | 'image' | 'text' | 'analysis', OperationType> = {
  audio: 'audio_block_generation',
  image: 'page_generation',
  text: 'block_regeneration',
  analysis: 'story_analysis',
};

export function useExactCost(
  operationType: 'audio' | 'image' | 'text' | 'analysis',
  count: number
): { cost: number; loading: boolean } {
  const opType = EXACT_OP_MAP[operationType];
  const line: EstimateLine = operationType === 'image' ? { opType, imageCount: 1 } : { opType };
  const { costs, loading } = useOperationCosts([line]);
  const perUnit = costs[0] ?? 0;

  const cost = useMemo(
    () => (operationType === 'analysis' ? perUnit : count * perUnit),
    [operationType, count, perUnit],
  );

  return { cost, loading };
}
